import { Link } from 'react-router-dom';
import {
  ArrowRight, AlertTriangle, Clock, Eye, Zap, Shield,
  ChevronRight, ArrowDown, Activity, Users, MonitorCheck
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const WhyTapelligencePage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  return (
    <div className="min-h-screen" data-testid="why-tapelligence-page">

      {/* ========== HERO ========== */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-br from-red-50 to-orange-50 rounded-full blur-3xl" />
        </div>
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-4 block" data-testid="why-label">
              {t('whyTapelligence.hero.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-slate-900 leading-tight tracking-tight mb-6" data-testid="why-title">
              {t('whyTapelligence.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              {t('whyTapelligence.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* ========== SECTION 1: BLIND SPOTS ========== */}
      <section className="section-padding bg-white" data-testid="blind-spots-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block">{t('whyTapelligence.blindSpots.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('whyTapelligence.blindSpots.title')}
              </h2>
            </div>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed mb-8">
              <p>{t('whyTapelligence.blindSpots.intro1')}</p>
              <p>{t('whyTapelligence.blindSpots.intro2')}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {(t('whyTapelligence.blindSpots.items') || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#0077FF]/5 rounded-xl p-4 border border-[#0077FF]/15">
                  <AlertTriangle className="w-5 h-5 text-[#FF7F00] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700">{item}</p>
                </div>
              ))}
            </div>
            <p className="text-base text-slate-600 mt-6">{t('whyTapelligence.blindSpots.closing')}</p>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: RISK LOOP ========== */}
      <section className="section-padding bg-slate-50" data-testid="risk-loop-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block">{t('whyTapelligence.riskLoop.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('whyTapelligence.riskLoop.title')}
              </h2>
            </div>

            {/* Flow diagram */}
            <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap md:flex-nowrap mb-8">
              {(t('whyTapelligence.riskLoop.steps') || []).map((step, i, arr) => {
                const icons = [AlertTriangle, Clock, Users, Shield, Activity];
                const Ic = icons[i];
                return (
                  <div key={i} className="flex items-center gap-2 md:gap-3">
                    <div className="flex items-center gap-2.5 bg-white rounded-xl border border-[#0077FF]/20 shadow-sm px-4 py-3">
                      <Ic className="w-4 h-4 text-[#FF7F00] flex-shrink-0" />
                      <span className="text-sm text-slate-700 font-medium whitespace-nowrap">{step}</span>
                    </div>
                    {i < arr.length - 1 && <ChevronRight className="w-5 h-5 text-[#0077FF]/40 flex-shrink-0 hidden md:block" />}
                    {i < arr.length - 1 && <ArrowDown className="w-5 h-5 text-[#0077FF]/40 flex-shrink-0 md:hidden" />}
                  </div>
                );
              })}
            </div>

            <div className="space-y-3 text-base text-slate-600 leading-relaxed text-center max-w-3xl mx-auto">
              <p>{t('whyTapelligence.riskLoop.explanation1')}</p>
              <p>{t('whyTapelligence.riskLoop.explanation2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: HOW TAPELLIGENCE CHANGES THIS ========== */}
      <section className="section-padding bg-white" data-testid="how-changes-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('whyTapelligence.howChanges.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('whyTapelligence.howChanges.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(t('whyTapelligence.howChanges.cards') || []).map((card, i) => {
              const icons = [Zap, Eye, MonitorCheck];
              const colors = ['text-[#FF7F00] bg-[#FF7F00]/10 border-[#FF7F00]/20', 'text-[#0077FF] bg-[#0077FF]/10 border-[#0077FF]/20', 'text-emerald-500 bg-emerald-50 border-emerald-200'];
              const Ic = icons[i];
              return (
                <div key={i} className={`bg-white rounded-2xl p-6 border ${colors[i].split(' ')[2]} shadow-sm`} data-testid={`how-card-${i}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${colors[i].split(' ').slice(1, 2).join(' ')}`}>
                    <Ic className={`w-5 h-5 ${colors[i].split(' ')[0]}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{card.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{card.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: THE RESULT ========== */}
      <section className="section-padding bg-slate-900" data-testid="result-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
              {t('whyTapelligence.result.statement')}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {(t('whyTapelligence.result.metrics') || []).map((m, i) => (
                <div key={i} className="text-center" data-testid={`result-metric-${i}`}>
                  <p className="text-2xl md:text-3xl font-bold text-white font-mono leading-none mb-2">{m.value}</p>
                  <p className="text-sm text-[#4DB8FF]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="section-padding bg-white" data-testid="why-cta-section">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <p className="text-lg text-slate-600 mb-6">{t('whyTapelligence.cta.description')}</p>
          <Link
            to={getLocalizedPath('/implementation')}
            className="inline-flex items-center gap-2 bg-[#FF7F00] text-white px-8 py-3 rounded-full hover:bg-[#E67200] transition-colors font-medium"
            data-testid="why-cta-btn"
          >
            {t('whyTapelligence.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default WhyTapelligencePage;
